import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Link,
  Box,
  Alert,
  Grid,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Email as EmailIcon, Lock as LockIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import PageLayout from '../components/PageLayout';
import ForgotPasswordDialog from '../components/ForgotPasswordDialog';
import { trackUserLogin, trackPageView, trackFormSubmission } from '../utils/analytics';
import Seo from '../components/Seo';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Track page view on component mount
  useEffect(() => {
    trackPageView('/login', 'Login');
  }, []);

  // Handle initial state from navigation (success message or open forgot password)
  useEffect(() => {
    if (location.state?.message) {
      // Show success message (like from password reset)
      setError(''); // Clear any existing errors
    }
    if (location.state?.openForgotPassword) {
      setShowForgotPassword(true);
    }
  }, [location.state]);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await login(email, password);
      
      if (response && response.success) {
        const user = response.user;
        trackFormSubmission('login', true);
        trackUserLogin('email');
        
        // Enhanced role checking to match patterns used throughout the app
        const isAdmin = user.role && (
          typeof user.role === 'object' 
            ? user.role.name === 'ADMIN' 
            : user.role === 'ADMIN'
        );
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Login - User role:', user.role);
          console.log('Login - Is admin:', isAdmin);
        }
        
        const isMaid = user.role && (
          typeof user.role === 'object'
            ? user.role.name === 'MAID'
            : user.role === 'MAID'
        );

        if (isAdmin) {
          if (process.env.NODE_ENV === 'development') {
            console.log('Login - Redirecting admin user to dashboard');
          }
          navigate('/dashboard');
        } else if (isMaid) {
          navigate('/maid-portal');
        } else {
          // Check for returnTo in state
          const returnTo = location.state?.returnTo;
          const preserveFlow = location.state?.preserveFlow;

          if (returnTo) {
            // Navigate to returnTo with preserveFlow if it exists
            navigate(returnTo, {
              state: preserveFlow ? { preserveFlow: true } : undefined,
              replace: true
            });
          } else {
            // Default navigation if no returnTo
            const from = location.state?.from?.pathname;
            if (from && from !== '/login') {
              navigate(from);
            } else {
              navigate('/bookings');
            }
          }
        }
      } else {
        setError(response?.error || 'Failed to login');
        trackFormSubmission('login', false);
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.response?.data?.error || 'Failed to login');
      trackFormSubmission('login', false);
    }
  };

  return (
    <PageLayout>
      <Seo title="Sign In" path="/login" noindex />
      <Grid container component="main" sx={{ height: '100vh' }}>
      <Grid
        item
        xs={false}
        sm={4}
        md={7}
        sx={{
          backgroundImage: 'url(/images/cleaning-hero.jpg)',
          backgroundRepeat: 'no-repeat',
          backgroundColor: (t) =>
            t.palette.mode === 'light' ? t.palette.grey[50] : t.palette.grey[900],
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
        <Box
          sx={{
            my: 8,
            mx: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <img src="/images/logo2.jpg" alt="PrimeShine Logo" style={{ width: '150px', marginBottom: '1rem' }} />
          <Typography component="h1" variant="h5">
            Sign in
          </Typography>
          {location.state?.message && (
            <Alert severity="success" sx={{ mt: 2, width: '100%' }}>
              {location.state.message}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
              {error}
            </Alert>
          )}
          <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
            >
              Sign In
            </Button>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Link 
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowForgotPassword(true);
                  }}
                  sx={{ textDecoration: 'underline' }}
                >
                  Forgot your password?
                </Link>
              </Grid>
              <Grid item xs={12} sm={6} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                <Link component={RouterLink} to="/register" variant="body2">
                  {"Don't have an account? Sign Up"}
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Box>
        
        {/* Forgot Password Dialog */}
        <ForgotPasswordDialog
          open={showForgotPassword}
          onClose={() => setShowForgotPassword(false)}
        />
      </Grid>
    </Grid>
    </PageLayout>
  );
};

export default Login; 